import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatCard } from './components/StatCard';
import { SectionStatusChip } from './components/SectionStatusChip';
import { ViewControls } from './components/ViewControls';
import { KpiTimeChart } from './components/KpiTimeChart';
import { SectionExportButton } from './components/SectionExportButton';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import { fmtNum, fmtNumShort, fmtPeriodo, CHART_COLORS, calcDelta, prevPeriodo } from './components/kpiUtils';
import { Gift, Users, Store, Tags, Upload, TrendingUp } from 'lucide-react';

const CANJE = '#10b981';
const USER = '#0ea5e9';

const KpisAdopcion = () => {
  const [data, setData] = useState({ totales: {}, por_mes: [], por_partner: [], por_categoria: [], recientes: [], registros: [], puede_cargar: false });
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const rootRef = useRef(null);
  const [view, setView] = useState('evolucion');
  const [chartType, setChartType] = useState('linea');
  const [mesUno, setMesUno] = useState('');
  const [mesA, setMesA] = useState('');
  const [mesB, setMesB] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/kpis/bonda');
      setData(r.data);
      const ps = (r.data.por_mes || []).map((m) => m.periodo);
      if (ps.length) {
        setMesUno((p) => p || ps[ps.length - 1]);
        setMesB((p) => p || ps[ps.length - 1]);
        setMesA((p) => p || ps[ps.length - 2] || ps[ps.length - 1]);
      }
    } catch (e) { toast.error('Error al cargar Bonda'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const { totales, por_mes, por_partner, por_categoria, recientes, registros, puede_cargar } = data;
  const periodos = useMemo(() => (por_mes || []).map((m) => m.periodo), [por_mes]);

  const subirArchivo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/kpis/bonda/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.insertados} canjes nuevos · ${r.data.duplicados} duplicados omitidos`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar el archivo'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const aggFor = (per) => {
    const rows = (registros || []).filter((r) => !per || r.periodo === per);
    const users = new Set(); const partners = {}; const cats = {};
    rows.forEach((r) => { if (r.usuario) users.add(r.usuario); partners[r.partner] = (partners[r.partner] || 0) + 1; cats[r.categoria] = (cats[r.categoria] || 0) + 1; });
    const topP = Object.entries(partners).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([partner, canjes]) => ({ partner, canjes }));
    const topC = Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([categoria, canjes]) => ({ categoria, canjes }));
    return { canjes: rows.length, usuarios: users.size, partners: Object.keys(partners).length, categorias: Object.keys(cats).length, topP, topC, top: topP[0] };
  };

  const evoRows = useMemo(() => (por_mes || []).map((m) => ({ label: fmtPeriodo(m.periodo, true), canjes: m.canjes, usuarios_unicos: m.usuarios_unicos })), [por_mes]);
  const un = useMemo(() => aggFor(mesUno), [registros, mesUno]);
  const unPrev = useMemo(() => aggFor(prevPeriodo(mesUno)), [registros, mesUno]);
  const aggA = useMemo(() => aggFor(mesA), [registros, mesA]);
  const aggB = useMemo(() => aggFor(mesB), [registros, mesB]);
  const cmpRows = [
    { label: 'Canjes', A: aggA.canjes, B: aggB.canjes },
    { label: 'Usuarios únicos', A: aggA.usuarios, B: aggB.usuarios },
  ];

  // top sections según vista
  const topPartners = view === 'un-mes' ? un.topP : (por_partner || []);
  const topCats = view === 'un-mes' ? un.topC : (por_categoria || []);

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-adopcion-loading">Cargando Bonda…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-adopcion">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-emerald-500/10"><Gift className="h-6 w-6 text-emerald-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Bonda</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-adopcion" /></div>
            <p className="text-sm text-slate-500">Uso de beneficios Bonda (canjes de cupones)</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="bonda" testId="bonda-help-btn" />
          <SectionExportButton title="Bonda" targetRef={rootRef} testId="bonda-export-btn" />
          {puede_cargar && (
            <>
              <Button onClick={() => fileRef.current?.click()} disabled={importing} className="bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition-transform" data-testid="bonda-subir-btn">
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Importando…' : 'Subir CSV / XLSX'}
              </Button>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={subirArchivo} data-testid="bonda-file-input" />
            </>
          )}
        </div>
      </div>

      {/* KPIs por vista */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {view === 'comparar' ? (
          <>
            <StatCard label={`Canjes ${fmtPeriodo(mesA)}`} value={fmtNum(aggA.canjes)} icon={Gift} accent="#4f46e5" testId="bonda-kpi-canjes" />
            <StatCard label={`Canjes ${fmtPeriodo(mesB)}`} value={fmtNum(aggB.canjes)} icon={Gift} accent="#0ea5e9" testId="bonda-kpi-canjes-b" />
            <StatCard label="Variación canjes" value={calcDelta(aggB.canjes, aggA.canjes) ? `${calcDelta(aggB.canjes, aggA.canjes).pct >= 0 ? '+' : ''}${calcDelta(aggB.canjes, aggA.canjes).pct.toFixed(1)}%` : '—'} icon={TrendingUp} accent="#10b981" delta={calcDelta(aggB.canjes, aggA.canjes)} testId="bonda-kpi-var" />
            <StatCard label="Usuarios" value={`${fmtNum(aggA.usuarios)} → ${fmtNum(aggB.usuarios)}`} icon={Users} accent="#3b82f6" delta={calcDelta(aggB.usuarios, aggA.usuarios)} testId="bonda-kpi-usuarios" />
          </>
        ) : view === 'un-mes' ? (
          <>
            <StatCard label={`Canjes ${fmtPeriodo(mesUno)}`} value={fmtNum(un.canjes)} icon={Gift} accent="#10b981" delta={calcDelta(un.canjes, unPrev.canjes)} deltaLabel="vs mes ant." testId="bonda-kpi-canjes" />
            <StatCard label="Usuarios únicos" value={fmtNum(un.usuarios)} icon={Users} accent={USER} delta={calcDelta(un.usuarios, unPrev.usuarios)} testId="bonda-kpi-usuarios" />
            <StatCard label="Partners" value={fmtNum(un.partners)} icon={Store} accent="#6366f1" testId="bonda-kpi-partners" />
            <StatCard label="Partner top" value={un.top?.partner || '—'} icon={Store} accent="#3b82f6" deltaLabel={un.top ? `${un.top.canjes} canjes` : ''} testId="bonda-kpi-categorias" />
          </>
        ) : (
          <>
            <StatCard label="Canjes" value={fmtNum(totales?.canjes)} icon={Gift} accent="#10b981" testId="bonda-kpi-canjes" />
            <StatCard label="Usuarios únicos" value={fmtNum(totales?.usuarios_unicos)} icon={Users} accent={USER} testId="bonda-kpi-usuarios" />
            <StatCard label="Partners" value={fmtNum(totales?.partners)} icon={Store} accent="#6366f1" testId="bonda-kpi-partners" />
            <StatCard label="Categorías" value={fmtNum(totales?.categorias)} icon={Tags} accent="#3b82f6" testId="bonda-kpi-categorias" />
          </>
        )}
      </div>

      {/* Chart card */}
      <Card className="p-5 lg:p-6 mb-6" data-testid="bonda-chart-card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
          <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-200">
            {view === 'comparar' ? 'Comparación de meses' : view === 'un-mes' ? `Top partners · ${fmtPeriodo(mesUno)}` : 'Evolución mensual'}
          </h2>
          <ViewControls view={view} onView={setView} chartType={chartType} onChartType={setChartType}
            periodos={periodos} mesUno={mesUno} onMesUno={setMesUno} mesA={mesA} onMesA={setMesA} mesB={mesB} onMesB={setMesB}
            showChartType={view === 'evolucion'} />
        </div>

        {view === 'evolucion' && (
          <KpiTimeChart data={evoRows} type={chartType} stacked={false}
            series={[{ key: 'canjes', name: 'Canjes', color: CANJE }, { key: 'usuarios_unicos', name: 'Usuarios únicos', color: USER }]}
            valueFormatter={fmtNum} axisFormatter={fmtNumShort} testId="bonda-chart-evolucion" />
        )}
        {view === 'un-mes' && (
          <KpiTimeChart data={un.topP} xKey="partner" series={[{ key: 'canjes', name: 'Canjes', color: CANJE }]}
            type="barras" barLayout="vertical" cellColors={CHART_COLORS} height={Math.max(220, un.topP.length * 40)}
            valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="bonda-chart-mes" />
        )}
        {view === 'comparar' && (
          <KpiTimeChart data={cmpRows} xKey="label"
            series={[{ key: 'A', name: fmtPeriodo(mesA), color: '#4f46e5' }, { key: 'B', name: fmtPeriodo(mesB), color: '#0ea5e9' }]}
            type="barras" valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={300} testId="bonda-chart-comparar" />
        )}
      </Card>

      {/* Top partners & categorías (evolución y un-mes) */}
      {view !== 'comparar' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card className="p-5 lg:p-6" data-testid="bonda-top-partners">
            <h2 className="text-base font-semibold tracking-tight text-slate-800 dark:text-slate-200 mb-4">Top partners{view === 'un-mes' ? ` · ${fmtPeriodo(mesUno)}` : ''}</h2>
            <KpiTimeChart data={topPartners} xKey="partner" series={[{ key: 'canjes', name: 'Canjes', color: CANJE }]}
              type="barras" barLayout="vertical" cellColors={CHART_COLORS} height={Math.max(200, (topPartners.length || 1) * 34)}
              valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="bonda-top-partners-chart" />
          </Card>
          <Card className="p-5 lg:p-6" data-testid="bonda-top-categorias">
            <h2 className="text-base font-semibold tracking-tight text-slate-800 dark:text-slate-200 mb-4">Top categorías{view === 'un-mes' ? ` · ${fmtPeriodo(mesUno)}` : ''}</h2>
            <KpiTimeChart data={topCats} xKey="categoria" series={[{ key: 'canjes', name: 'Canjes', color: '#6366f1' }]}
              type="barras" barLayout="vertical" height={Math.max(200, (topCats.length || 1) * 34)}
              valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="bonda-top-categorias-chart" />
          </Card>
        </div>
      )}

      {/* Canjes recientes */}
      <Card className="p-0 overflow-hidden" data-testid="bonda-recientes">
        <div className="p-5 border-b border-slate-100 dark:border-slate-800">
          <h2 className="text-base font-semibold tracking-tight text-slate-800 dark:text-slate-200">Canjes recientes</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                <th className="text-left font-medium px-4 py-2.5">Fecha</th>
                <th className="text-left font-medium px-4 py-2.5">Usuario</th>
                <th className="text-left font-medium px-4 py-2.5">Partner</th>
                <th className="text-left font-medium px-4 py-2.5">Categoría</th>
                <th className="text-left font-medium px-4 py-2.5">Beneficio</th>
                <th className="text-left font-medium px-4 py-2.5">Canal</th>
              </tr>
            </thead>
            <tbody>
              {(recientes || []).map((r, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-2.5 whitespace-nowrap text-slate-500">{r.fecha || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.usuario || '—'}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-700 dark:text-slate-200">{r.partner || '—'}</td>
                  <td className="px-4 py-2.5">{r.categoria || '—'}</td>
                  <td className="px-4 py-2.5">{r.microtag || '—'}</td>
                  <td className="px-4 py-2.5 capitalize">{r.disponible_en || '—'}</td>
                </tr>
              ))}
              {(recientes || []).length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No hay canjes cargados.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default KpisAdopcion;
